import { useState } from 'react';
import { Bell, BellOff, Smartphone, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { isWingmanSupported, enableWingmanPush, sendTestPush } from './wingmanPush';
import { devError } from '../utils/logger';

export default function WingmanSettingsPage() {
  const [pushBusy, setPushBusy] = useState(false);

  const enable = async () => {
    setPushBusy(true);
    try {
      if (!isWingmanSupported()) {
        toast.error('Push needs Chrome/Edge/Firefox on Android.');
        return;
      }
      const { ok, reason } = await enableWingmanPush();
      if (ok) toast.success('Push enabled ✓');
      else toast.error(`Failed: ${reason}`);
    } finally {
      setPushBusy(false);
    }
  };

  const test = async () => {
    try {
      const r = await sendTestPush();
      if (r?.sent > 0) toast.success(`Test push sent to ${r.sent} device(s) ✓`);
      else toast.info("No subscribed devices yet — tap 'Enable push' first.");
    } catch (e) {
      devError('test push failed', e);
      toast.error("Couldn't send test push");
    }
  };

  return (
    <div className="px-5 py-6 space-y-6" data-testid="wingman-settings">
      <h2 className="text-xl font-bold text-white">Settings</h2>

      <section className="rounded-2xl bg-white/5 border border-white/5 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-semibold mb-1">Push notifications</h3>
            <p className="text-sm text-slate-400">Get pinged the second a buyer enquires — even when the app is closed.</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={enable} disabled={pushBusy} className="px-4 py-2.5 rounded-full bg-[#D4AF37] text-black font-semibold text-sm" data-testid="wingman-settings-enable-push">
            Enable push
          </button>
          <button onClick={test} className="px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-slate-200 text-sm" data-testid="wingman-settings-test-push">
            Send test
          </button>
        </div>
      </section>

      <section className="rounded-2xl bg-white/5 border border-white/5 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-semibold mb-1">Install Wingman</h3>
            <p className="text-sm text-slate-400">Tap your browser menu (⋮) → <b className="text-slate-200">Add to Home screen</b> for an app-like experience.</p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-white/5 border border-white/5 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
            <ExternalLink className="w-5 h-5 text-slate-300" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-semibold mb-1">Need more?</h3>
            <p className="text-sm text-slate-400">Open the full supplier dashboard on your desktop for analytics, payouts, listing edits and storefront.</p>
            <a href="/supplier/dashboard" className="text-[#D4AF37] text-sm mt-2 inline-block hover:underline" data-testid="wingman-settings-open-desktop">
              Open desktop dashboard →
            </a>
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-white/5 border border-white/5 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
            <BellOff className="w-5 h-5 text-red-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-semibold mb-1">Notifications not working?</h3>
            <p className="text-sm text-slate-400">Open Chrome → ⋮ → Site settings → Notifications → allow PatinaMart. Then come back and tap <b className="text-slate-200">Enable push</b>.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
